import { useMutation } from "@apollo/client";
import { ChangeEventHandler, useEffect, useState } from "react";
import { GetAccountQuery } from "types/graphql-schema";

import { currentUserVar } from "@cache/cache";
import Button from "@components/button/button";
import { buttonTheme } from "@components/button/button-constants";
import ComboboxGeneric, {
  ComboboxGenericOption,
} from "@components/combobox/generic-combobox";
import Heading from "@components/heading/heading";
import Loading from "@components/loading/loading";
import SettingsLayout from "@components/settings-layout/settings-layout";
import CustomTextareaAutosize from "@components/textarea-autosize/textarea-autosize";
import { textareaClassName } from "@helpers/css";
import { assertNonNull } from "@helpers/helpers";
import timezones from "@helpers/timezones";

import updateUserMutation from "../graphql/update-user-mutation";

const linkOptions = [
  {
    value: "always",
    label: "For all events",
  },
  {
    value: "meetings_with_topics",
    label: "Only for events with agenda items in Topicflow",
  },
  {
    value: "never",
    label: "Never add links to my calendar events",
  },
];

const Preferences = ({ me }: { me: GetAccountQuery["me"] }) => {
  const currentUser = currentUserVar();
  const selectedLinkOption = currentUser.eventLinkInDescriptionPreference;
  const timezoneOptions = timezones.map((timezone) => ({
    label: timezone,
    value: timezone,
  }));

  const [timezone, setTimezone] = useState<string | null | undefined>(null);
  const [aiGuidelines, setAiGuidelines] = useState("");
  const [updateUser, { loading }] = useMutation(updateUserMutation);

  const handleChangeTimezone = (option: ComboboxGenericOption<string>) => {
    if (!option) return;
    const value = option.value;
    setTimezone(value);
    updateUser({
      variables: { userId: assertNonNull(me).id, timezone: value },
    });
  };

  const handleChangeSelectedLinkOption: ChangeEventHandler<HTMLInputElement> = (
    e,
  ) => {
    updateUser({
      variables: {
        userId: currentUser.id,
        eventLinkInDescriptionPreference: e.target.value,
      },
    });
  };

  const handleSaveAiGuidelines = async () => {
    if (assertNonNull(me).aiGuidelines === aiGuidelines) return;

    await updateUser({
      variables: {
        userId: assertNonNull(me).id,
        aiGuidelines: aiGuidelines,
      },
    });
  };

  const handleCancelAiGuidelines = () => {
    setAiGuidelines(assertNonNull(me).aiGuidelines);
  };

  useEffect(() => {
    setTimezone(assertNonNull(me).timezone);
  }, [me]);

  useEffect(() => {
    setAiGuidelines(assertNonNull(me).aiGuidelines || "");
  }, [me!.aiGuidelines]);

  return (
    <div>
      <Heading small title="Preferences" />

      <SettingsLayout.Container>
        <SettingsLayout.Row>
          <SettingsLayout.Left label="* Only affects events where you are the organizer">
            Automatically add a Topicflow link to calendar events
          </SettingsLayout.Left>
          <SettingsLayout.Right>
            <div className="flex flex-col">
              {linkOptions.map(({ value, label }) => (
                <div
                  key={value}
                  className="text-sm text-gray-800 flex items-center py-1"
                >
                  <input
                    type="radio"
                    name="link-option"
                    id={`input-link-option-${value}`}
                    value={value}
                    defaultChecked={selectedLinkOption === value}
                    onChange={handleChangeSelectedLinkOption}
                  />
                  <label
                    className="ml-2 cursor-pointer"
                    htmlFor={`input-link-option-${value}`}
                  >
                    {label}
                  </label>
                </div>
              ))}
              <div className="text-gray-500 text-sm mt-2">
                Events created using Topicflow will always include a link to the
                agenda.
              </div>
            </div>
          </SettingsLayout.Right>
        </SettingsLayout.Row>

        <SettingsLayout.Row>
          <SettingsLayout.Left>Timezone</SettingsLayout.Left>
          <SettingsLayout.Right>
            <div className="lg:w-96">
              {timezone && (
                <ComboboxGeneric<string>
                  value={
                    timezoneOptions.find(
                      (option) => option.value === timezone,
                    ) || timezoneOptions[0]
                  }
                  options={timezoneOptions}
                  onChangeValue={handleChangeTimezone}
                  aria-label="Timezone select"
                />
              )}
            </div>
          </SettingsLayout.Right>
        </SettingsLayout.Row>

        {waffle.flag_is_active("ask-ai-guidelines") && (
          <SettingsLayout.Row>
            <SettingsLayout.Left>Ask AI Guidelines</SettingsLayout.Left>
            <SettingsLayout.Right>
              <div className="w-full space-y-4">
                <div className="text-gray-500 text-sm mb-2">
                  Add guidelines specific to your account to personalize Ask AI.
                </div>
                <CustomTextareaAutosize
                  className={textareaClassName}
                  placeholder="Enter guidelines for how Ask AI should respond to your queries..."
                  value={aiGuidelines}
                  onChange={(e) => setAiGuidelines(e.target.value)}
                  minRows={4}
                  aria-label="Personal AI guidelines"
                  disabled={loading}
                />
                {assertNonNull(me).aiGuidelines !== aiGuidelines && (
                  <div className="flex items-center justify-end gap-2">
                    <Button
                      text="Cancel"
                      onClick={handleCancelAiGuidelines}
                      disabled={loading}
                    />
                    <Button
                      text="Save"
                      theme={buttonTheme.primary}
                      onClick={handleSaveAiGuidelines}
                      disabled={loading}
                    />
                    {loading && <Loading mini size="4" />}
                  </div>
                )}
              </div>
            </SettingsLayout.Right>
          </SettingsLayout.Row>
        )}
      </SettingsLayout.Container>
    </div>
  );
};

export default Preferences;
